// Creatures in the Inkwell style. The server keeps its own ids for creatures (shared/mobs.ts); each one is drawn
// as one of the Inkwell bestiary.
import ink from '../ink/ink.js';
import { mobDef } from '../../../shared/mobs.js';
import { conOf } from '../../../shared/difficulty.js';

const I = ink as any;
const { INK, rrect, MONO, FONT, clamp } = I;
const K = 3;
export const INK_MOB: Record<string, string> = {
  puff: 'mossling', bunny: 'moonhare', beetle: 'pebblecrab', shroom: 'lanterncap', wolf: 'imp', boar: 'duneroller', bramblehorn: 'briardrake',
  ghoul: 'teacup', wisp: 'inkveil', boneknight: 'rattlebone', gravekeeper: 'lastcandle', ember_wyrm: 'stareater',
  dreamhare: 'moonhare', nightimp: 'imp', cinderbat: 'cinderbat', wasp: 'wasp', frostmaw: 'frostmaw', rootjaw: 'rootjaw',
  trial_knight: 'bellkeeper', trial_wizard: 'glassfin', trial_cleric: 'hollowbride', trial_rogue: 'maskdancer',
};
/** The Inkwell creature a kind is drawn as: its own art, or (a recolour) the art of the creature it borrows. */
export const crOf = (kind: string | undefined): any => I.CR[INK_MOB[kind ?? ''] ?? INK_MOB[mobDef(kind ?? '')?.sprite ?? ''] ?? kind ?? ''] ?? I.CR.mossling;
/** Recolours: drawn off to the side, shifted to their colour (keeping the ink lines and shading), then stamped. */
const TS = 360, TOX = 180, TOY = 300;
let tA: HTMLCanvasElement | null = null, tB: HTMLCanvasElement | null = null;
function tinted(g: CanvasRenderingContext2D, tint: string, draw: (c: CanvasRenderingContext2D) => void): void {
  if (!tA) { tA = document.createElement('canvas'); tB = document.createElement('canvas'); tA.width = tB!.width = TS; tA.height = tB!.height = TS; }
  const a = tA.getContext('2d')!, b = tB!.getContext('2d')!;
  a.setTransform(1, 0, 0, 1, 0, 0); a.clearRect(0, 0, TS, TS); a.translate(TOX, TOY); a.lineJoin = 'round'; a.lineCap = 'round'; draw(a);
  b.setTransform(1, 0, 0, 1, 0, 0); b.globalCompositeOperation = 'copy'; b.drawImage(tA, 0, 0);
  b.globalCompositeOperation = 'color'; b.globalAlpha = 0.78; b.fillStyle = tint; b.fillRect(0, 0, TS, TS);
  b.globalAlpha = 1; b.globalCompositeOperation = 'destination-in'; b.drawImage(tA, 0, 0); b.globalCompositeOperation = 'source-over';
  g.drawImage(tB!, -TOX, -TOY);
}
/** Out in the world creatures are drawn a little smaller than in Inkwell, so a big one doesn't fill a meadow. */
const WORLD_K = 0.9;
/** The top of a creature above its feet, in game pixels. */
export function mobTop(kind: string | undefined): number { if (kind === 'shadow') return 12; const d = crOf(kind); return (d.h * d.k * WORLD_K + (d.hover ?? 0)) / K; }

export interface MobView { kind: string; x: number; y: number; dir: number; moving: boolean; attackK: number; hp: number; hurtAt: number; deadAt: number; id: number }
/** Draw a creature with its feet at (x, y), game pixels. */
/** A wandering shadow on the world map: a swirl of ink with two eyes, rustling the grass as it goes. */
function drawShadow(g: CanvasRenderingContext2D, m: MobView, now: number): void {
  const t = now / 1000 + m.id, dead = m.deadAt ? clamp((now - m.deadAt) / 500, 0, 1) : 0; if (dead >= 1) return;
  g.save(); g.translate(m.x, m.y); g.globalAlpha = 1 - dead;
  g.fillStyle = 'rgba(62,47,40,0.22)'; g.beginPath(); g.ellipse(0, 0.5, 8, 2.6, 0, 0, Math.PI * 2); g.fill();
  for (let i = 0; i < 6; i++) { const a = t * 2 + i, r = 3.5 + Math.sin(t * 3 + i) * 1.2; g.fillStyle = `rgba(62,47,40,${0.5 + (i % 2) * 0.2})`; g.beginPath(); g.arc(Math.cos(a) * 3, -5 + Math.sin(a * 1.3) * 2, r, 0, Math.PI * 2); g.fill(); }
  const blink = Math.sin(t * 1.7) > 0.95 ? 0.2 : 1, dx = m.dir === 1 ? 1 : m.dir === 3 ? -1 : 0;
  g.fillStyle = '#fbf6ea'; for (const s of [-1, 1]) { g.beginPath(); g.ellipse(s * 2 + dx, -6, 1, blink, 0, 0, Math.PI * 2); g.fill(); }
  if (m.moving) for (let i = 0; i < 3; i++) { const ph = (t * 2 + i / 3) % 1; g.strokeStyle = `rgba(110,150,80,${1 - ph})`; g.lineWidth = 0.7; g.beginPath(); g.moveTo(-6 + i * 6, 0); g.lineTo(-7 + i * 6 - dx * ph * 3, -3 - ph * 3); g.stroke(); }
  g.restore();
}
export function drawMob(g: CanvasRenderingContext2D, m: MobView, now: number): void {
  if (m.kind === 'shadow') return drawShadow(g, m, now);
  const def = crOf(m.kind), dead = m.deadAt ? clamp((now - m.deadAt) / 700, 0, 1) : 0;
  if (dead >= 1) return;
  const hurt = m.hurtAt ? Math.max(0, 1 - (now - m.hurtAt) / 220) : 0, face = m.dir === 3 ? -1 : m.dir === 1 ? 1 : (m.id % 2 ? 1 : -1);
  g.save(); g.translate(m.x, m.y); g.scale(1 / K, 1 / K);
  g.globalAlpha = 1 - dead;
  if (hurt > 0 && Math.floor(now / 50) % 2) g.globalAlpha *= 0.65;
  const tint = mobDef(m.kind)?.tint, opts = { k: WORLD_K * (1 - dead * 0.4), mv: m.moving, atk: m.attackK, squash: hurt * 0.12 };
  if (tint) tinted(g, tint, c => I.drawCreature(c, def, 0, 0, face, now / 1000 + m.id * 0.31, opts));
  else I.drawCreature(g, def, 0, 0, face, now / 1000 + m.id * 0.31, opts);
  g.restore();
}

/** A creature's name card: level, name and a health bar under it (Inkwell's monster label). The name is in its
 *  "check" colour for someone of level `you` (Even Match is plain ink; Tough and up run yellow to red). */
export function drawMobLabel(g: CanvasRenderingContext2D, kind: string | undefined, x: number, y: number, hp: number, max: number, showName: boolean, you = 0): void {
  const md = mobDef(kind ?? ''); if (!md) return;
  const con = you ? conOf(md.level, you) : null;
  g.save(); g.translate(x, y); g.scale(1 / K, 1 / K);
  const t = showName ? `Lv. ${md.level} ${md.name}` : '';
  g.font = `500 13px ${MONO}`;
  const w = showName ? g.measureText(t).width + 18 : 56, hpK = clamp(hp / Math.max(1, max), 0, 1);
  if (showName) {
    g.fillStyle = 'rgba(251,246,234,0.92)'; rrect(g, -w / 2, -15, w, 26, 7); g.fill(); g.lineWidth = 1.6; g.strokeStyle = con && con.id !== 'even' ? con.color : INK; g.stroke();
    g.fillStyle = con ? con.color : md.boss || md.aggro ? '#a8433a' : INK; g.textAlign = 'center'; g.fillText(t, 0, 1);
    if (md.aggro || md.boss) { g.fillStyle = '#a8433a'; g.beginPath(); g.arc(-w / 2 + 6, -2, 2.4, 0, Math.PI * 2); g.fill(); }   // a red dot: it'll come for you
    if (hpK < 1) { g.fillStyle = '#e05a4c'; g.fillRect(-w / 2 + 8, 5, (w - 16) * hpK, 3); }
  } else {
    rrect(g, -w / 2, -3, w, 8, 4); g.fillStyle = 'rgba(251,246,234,0.9)'; g.fill(); g.lineWidth = 1.4; g.strokeStyle = INK; g.stroke();
    rrect(g, -w / 2 + 2, -1, (w - 4) * hpK, 4, 2); g.fillStyle = '#e05a4c'; g.fill();
  }
  g.restore();
  void FONT;
}
